/* ==========================================================================
   Namo Narayana — Cart Drawer behaviours
   · discount / coupon code apply + remove
   · quick-add from recommendation rails
   · expandable price breakdown
   · rotating promo strip
   Delegated listeners on document so everything survives the drawer being
   re-rendered by cart.js / cart-drawer.js section refreshes.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function money(cents) {
    if (window.Shopify && window.Shopify.formatMoney && window.nnCartMoneyFormat) {
      return window.Shopify.formatMoney(cents, window.nnCartMoneyFormat);
    }
    return '₹' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ---------- re-render the drawer from the server ---------- */
  function refreshDrawer() {
    return fetch(ROOT + '?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector('.drawer__inner');
        var dest = document.querySelector('#CartDrawer .drawer__inner');
        if (src && dest) dest.innerHTML = src.innerHTML;

        var drawer = document.querySelector('cart-drawer');
        var isEmpty = !doc.querySelector('.nnc-item');
        if (drawer) drawer.classList.toggle('is-empty', isEmpty);
        rehydrate();
      })
      .then(refreshCartBubble);
  }

  function refreshCartBubble() {
    return fetch(ROOT + '?section_id=cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector('.shopify-section');
        var dest = document.getElementById('cart-icon-bubble');
        if (src && dest) dest.innerHTML = src.innerHTML;
      })
      .catch(function () {});
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) drawer.open();
  }

  /* ---------- discount codes ----------
     /cart/update.js takes a `discount` parameter (Cart AJAX API, May 2025).
     Shopify prices the discount itself and returns the real cart, so the drawer
     shows the actual saving — whatever amount or percentage is configured in
     the admin — and it carries through to checkout on its own.

     Pass '' to remove. The response always returns 200: a code that doesn't
     apply comes back in `discount_codes` with `applicable: false`. */
  function setDiscount(code) {
    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code || '' }),
    }).then(function (r) {
      return r.json();
    });
  }

  function discountIsApplied(cart, code) {
    if (!cart) return false;
    var wanted = String(code || '').toUpperCase();
    var codes = cart.discount_codes || [];
    for (var i = 0; i < codes.length; i++) {
      if (String(codes[i].code || '').toUpperCase() === wanted) return codes[i].applicable === true;
    }
    return false;
  }

  function setMsg(wrap, text, kind) {
    var msg = wrap.querySelector('.nnc-coupon__msg');
    if (!msg) return;
    msg.textContent = text || '';
    msg.className = 'nnc-coupon__msg' + (kind ? ' nnc-coupon__msg--' + kind : '');
  }

  function handleApply(wrap, codeFromChip) {
    var input = wrap.querySelector('.nnc-coupon__input');
    var btn = wrap.querySelector('.nnc-coupon__apply');
    var code = (codeFromChip || (input && input.value) || '').trim();

    if (!code) {
      setMsg(wrap, 'Please enter a coupon code.', 'err');
      if (input) input.focus();
      return;
    }
    if (btn) {
      btn.disabled = true;
      btn.dataset.label = btn.textContent;
      btn.textContent = 'Applying…';
    }
    setMsg(wrap, '', '');

    setDiscount(code)
      .then(function (cart) {
        if (discountIsApplied(cart, code)) return refreshDrawer();
        // Clear the rejected code so it doesn't linger on the cart.
        return setDiscount('').then(function () {
          setMsg(wrap, 'This code isn’t valid for the items in your cart.', 'err');
        });
      })
      .catch(function () {
        setMsg(wrap, 'Could not apply the code. Please try again.', 'err');
      })
      .finally(function () {
        if (btn && document.body.contains(btn)) {
          btn.disabled = false;
          btn.textContent = btn.dataset.label || 'Apply';
        }
      });
  }

  function handleRemove(wrap) {
    var btn = wrap.querySelector('.nnc-coupon__remove');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Removing…';
    }
    setDiscount('')
      .then(refreshDrawer)
      .catch(function () {
        if (btn && document.body.contains(btn)) {
          btn.disabled = false;
          btn.textContent = 'Remove';
        }
      });
  }

  /* ---------- recommendation rails ----------
     The rail's Add buttons carry data-variant-id, so nn-sticky-cart.liquid's
     NNCart owns them: it adds to cart and swaps itself for a qty stepper, same
     as everywhere else on the site. We only have to (re)bind after a render. */
  function bindRecoButtons() {
    var drawer = document.getElementById('CartDrawer');
    if (drawer && window.NNCart && typeof window.NNCart.initButtons === 'function') {
      window.NNCart.initButtons(drawer);
      window.NNCart.refresh();
    }
  }

  /* ---------- delivery estimate by PIN code ----------
     Talks to the Vercel proxy (api/serviceability), which wraps Shiprocket.
     The PIN code is remembered so a returning shopper doesn't retype it. */
  var ETA_KEY = 'nnDeliveryPincode';

  function etaSay(wrap, html, kind) {
    var out = wrap.querySelector('[data-nn-eta-result]');
    if (!out) return;
    out.innerHTML = html || '';
    out.className = 'nnc-eta__result' + (kind ? ' nnc-eta__result--' + kind : '');
  }

  function etaPlural(n, word) {
    return n + ' ' + word + (n === 1 ? '' : 's');
  }

  function checkPincode(wrap, pincode) {
    var input = wrap.querySelector('[data-nn-eta-input]');
    var btn = wrap.querySelector('[data-nn-eta-check]');
    var code = String(pincode || (input && input.value) || '').trim();

    if (!/^\d{6}$/.test(code)) {
      etaSay(wrap, 'Please enter a valid 6-digit PIN code.', 'err');
      if (input) input.focus();
      return;
    }
    if (btn) btn.disabled = true;
    etaSay(wrap, 'Checking…', '');

    /* Shiprocket only knows the couriers wired into that account. We ship
       through others too, so a "not serviceable" answer — or the API being
       down — is not a reason to tell the shopper we can't deliver. Fall back
       to the configured flat estimate and only ever surface a real ETA when
       Shiprocket gives us one. */
    var fallbackDays = parseInt(wrap.getAttribute('data-fallback-days'), 10) || 3;
    function showFallback() {
      try {
        localStorage.setItem(ETA_KEY, code);
      } catch (e) {}
      etaSay(wrap, '<strong>Delivery in ' + etaPlural(fallbackDays, 'day') + '</strong> · COD available', 'ok');
    }

    /* `v` busts any response a browser cached back when the API was briefly
       returning the wrong CORS header. Bump it if that ever happens again. */
    var api = (wrap.getAttribute('data-api') || '').replace(/\/$/, '');
    fetch(api + '/api/serviceability?pincode=' + encodeURIComponent(code) + '&v=2', {
      mode: 'cors',
      cache: 'no-store',
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (d) {
        if (!d || d.error || !d.serviceable || !d.eta_days) return showFallback();

        try {
          localStorage.setItem(ETA_KEY, code);
        } catch (e) {}

        var where = [d.city, d.state].filter(Boolean).join(', ');
        var html = '<strong>Delivery in ' + etaPlural(d.eta_days, 'day') + '</strong>';
        if (where) html += ' to ' + where;
        html += d.cod_available ? ' · COD available' : ' · Prepaid only';
        etaSay(wrap, html, 'ok');
      })
      .catch(showFallback)
      .finally(function () {
        if (btn && document.body.contains(btn)) btn.disabled = false;
      });
  }

  function restorePincode() {
    var wrap = document.querySelector('[data-nn-eta]');
    if (!wrap) return;
    var saved;
    try {
      saved = localStorage.getItem(ETA_KEY);
    } catch (e) {}
    if (!/^\d{6}$/.test(saved || '')) return;
    var input = wrap.querySelector('[data-nn-eta-input]');
    if (input) input.value = saved;
    checkPincode(wrap, saved);
  }

  /* ---------- rotating promo strip ---------- */
  var stripTimer = null;
  function startStrip() {
    if (stripTimer) clearInterval(stripTimer);
    var strip = document.querySelector('.nnc-strip');
    if (!strip) return;
    var items = strip.querySelectorAll('.nnc-strip__item');
    if (items.length < 2) return;
    var i = 0;
    stripTimer = setInterval(function () {
      if (!document.body.contains(strip)) {
        clearInterval(stripTimer);
        stripTimer = null;
        return;
      }
      items[i].classList.remove('is-on');
      i = (i + 1) % items.length;
      items[i].classList.add('is-on');
    }, 4000);
  }

  /* ---------- delegated events ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var apply = t.closest && t.closest('.nnc-coupon__apply');
    if (apply) {
      e.preventDefault();
      handleApply(apply.closest('.nnc-coupon'));
      return;
    }

    var chip = t.closest && t.closest('.nnc-coupon__chip');
    if (chip) {
      e.preventDefault();
      var wrap = chip.closest('.nnc-coupon');
      var input = wrap.querySelector('.nnc-coupon__input');
      if (input) input.value = chip.dataset.code || chip.textContent.trim();
      handleApply(wrap, chip.dataset.code || chip.textContent.trim());
      return;
    }

    var remove = t.closest && t.closest('.nnc-coupon__remove');
    if (remove) {
      e.preventDefault();
      handleRemove(remove.closest('.nnc-coupon'));
      return;
    }

    var etaBtn = t.closest && t.closest('[data-nn-eta-check]');
    if (etaBtn) {
      e.preventDefault();
      checkPincode(etaBtn.closest('[data-nn-eta]'));
      return;
    }

    var toggle = t.closest && t.closest('.nnc-foot__totalbtn');
    if (toggle) {
      e.preventDefault();
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.classList.toggle('is-open', !open);
    }
  });

  /* Checkout: carry the applied discount code in the URL.
     Submitting the cart form drops it — checkout loads at full price — but
     /checkout?discount=CODE applies it. See the comment in cart-drawer.liquid. */
  document.addEventListener('click', function (e) {
    var cta = e.target.closest && e.target.closest('#CartDrawer-Checkout');
    if (!cta || cta.disabled) return;
    var code = cta.getAttribute('data-nn-discount');
    if (!code) return; // no discount — let the form submit normally
    e.preventDefault();
    window.location.href = ROOT + 'checkout?discount=' + encodeURIComponent(code);
  });

  /* Any [data-nn-open-cart] link opens the drawer instead of /cart. The header
     icon is handled by cart-drawer.js itself (it binds #cart-icon-bubble). */
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-nn-open-cart]');
    if (!link) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(link);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var coupon = e.target.closest && e.target.closest('.nnc-coupon__input');
    if (coupon) {
      e.preventDefault();
      handleApply(coupon.closest('.nnc-coupon'));
      return;
    }
    var eta = e.target.closest && e.target.closest('[data-nn-eta-input]');
    if (eta) {
      e.preventDefault();
      checkPincode(eta.closest('[data-nn-eta]'));
    }
  });

  /* digits only in the PIN code box */
  document.addEventListener('input', function (e) {
    var eta = e.target.closest && e.target.closest('[data-nn-eta-input]');
    if (!eta) return;
    var clean = eta.value.replace(/\D/g, '').slice(0, 6);
    if (clean !== eta.value) eta.value = clean;
  });

  /* qty stepper: keep the native quantity-input contract cart.js listens for */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.nnc-qty button');
    if (!btn) return;
    e.preventDefault();
    var input = btn.parentElement.querySelector('input');
    if (!input) return;
    var step = parseInt(input.step, 10) || 1;
    var value = parseInt(input.value, 10) || 0;
    var min = parseInt(input.min, 10) || 0;
    var max = input.max ? parseInt(input.max, 10) : Infinity;
    var next = btn.name === 'plus' ? value + step : value - step;
    if (next < min) next = min;
    if (next > max) next = max;
    if (next === value) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- keep drawer / sticky bar / bubble in sync ----------
     Two systems touch the cart: Dawn's pub-sub (qty steppers, PDP forms) and
     NNCart's `cart:updated` DOM event (sticky bar + rail steppers). Bridge them
     so the coupon block and price breakdown — which live outside the section
     Dawn re-renders — never go stale. refreshDrawer() emits neither, so no loop. */
  var syncTimer = null;
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      refreshDrawer();
    }, 250);
  }

  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);

  function subscribeToDawn() {
    if (typeof window.subscribe !== 'function' || !window.PUB_SUB_EVENTS) return false;
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'nn-cart-drawer') return;
      scheduleSync();
    });
    return true;
  }
  if (!subscribeToDawn()) {
    document.addEventListener('DOMContentLoaded', subscribeToDawn);
  }

  /* Everything that has to run after the drawer's markup is (re)built.
     Exposed so callers that render the drawer themselves — the PDP buy box
     injects it straight from the /cart/add.js sections payload — get the same
     wiring without re-fetching the section. */
  function rehydrate() {
    startStrip();
    bindRecoButtons();
    restorePincode();
  }

  document.addEventListener('DOMContentLoaded', rehydrate);
  document.addEventListener('shopify:section:load', rehydrate);
  if (document.readyState !== 'loading') rehydrate();

  window.nnCartDrawer = {
    refresh: refreshDrawer,
    open: openDrawer,
    startStrip: startStrip,
    rehydrate: rehydrate,
  };
})();
